import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button, Table, Modal, Badge, Tabs, Tab } from "react-bootstrap";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FaEye, FaMapMarkerAlt } from "react-icons/fa";
import { GetEmployeeDailyAttList } from "../utils/queries";
import { convertDateFormat } from "../utils/helper";

const StaffDailyAttendanceListScreen = () => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [staffList, setStaffList] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("indoor");

  const { userInfo } = useSelector((state) => state.auth);
  const ReqCompanyCode = userInfo && userInfo[13]?.InfoField;

  const fetchData = async () => {
    try {
      const response = await GetEmployeeDailyAttList(convertDateFormat(selectedDate), "", ReqCompanyCode);
      const action = response[0]?.ActionType;
      if (action > 0) {
        const JSONData1 = JSON.parse(response[0]?.JSONData1);
        setStaffList(JSONData1);
      } else {
        toast.warn(response[0]?.ErrorMessage || "Failed to load data");
      }
    } catch (error) {
      console.error("Error fetching attendance:", error);
      toast.error("Failed to fetch attendance data");
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  const filteredStaffList = staffList.filter(staff => 
    staff.RangeType?.toLowerCase() === activeTab
  );

  const openMapWithLatLng = (latlng) => {
    if (!latlng) return;
    const [latitude, longitude] = latlng.split(',').map(coord => coord.trim());
    const url = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
    window.open(url, '_blank');
  };

  return (
    <Container className="py-4">
      <Row className="mb-3 align-items-center justify-content-between">
        <Col xs={12} md={6}>
          <h4 className="text-center">Daily Attendance</h4>
        </Col>
        <Col xs={12} md="auto" className="d-flex gap-2 mt-2 mt-md-0">
          <Form.Control 
            type="date" 
            value={selectedDate} 
            onChange={(e) => setSelectedDate(e.target.value)} 
          />
          <Button variant="primary" onClick={fetchData}>
            Refresh
          </Button>
        </Col>
      </Row>

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-3"
      >
        <Tab eventKey="indoor" title="Indoor">
          <div className="table-responsive">
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>View</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Entry Date</th>
                  <th>Entry Time</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredStaffList.map((staff) => (
                  <tr key={staff.AttID}>
                    <td>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          setSelectedMember(staff);
                          setShowModal(true);
                        }}
                      >
                        <FaEye />
                      </Button>
                    </td>
                    <td>{staff.EmployeeName}</td>
                    <td>{staff.AttType}</td>
                    <td>{new Date(staff.AttDateTime).toLocaleDateString()}</td>
                    <td>{new Date(staff.AttDateTime).toLocaleTimeString()}</td>
                    <td>
                      <Badge bg={
                        staff.reviewsts?.toLowerCase() === "approved" ? "success" :
                        staff.reviewsts?.toLowerCase() === "rejected" ? "danger" :
                        "warning"
                      }>
                        {staff.reviewsts || "Pending"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Tab>
        <Tab eventKey="outdoor" title="Outdoor">
          <div className="table-responsive">
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>View</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Entry Date</th>
                  <th>Entry Time</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredStaffList.map((staff) => (
                  <tr key={staff.AttID}>
                    <td>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          setSelectedMember(staff);
                          setShowModal(true);
                        }}
                      >
                        <FaEye />
                      </Button>
                    </td>
                    <td>{staff.EmployeeName}</td>
                    <td>{staff.AttType}</td>
                    <td>{new Date(staff.AttDateTime).toLocaleDateString()}</td>
                    <td>{new Date(staff.AttDateTime).toLocaleTimeString()}</td>
                    <td>
                      <Badge bg={
                        staff.reviewsts?.toLowerCase() === "approved" ? "success" :
                        staff.reviewsts?.toLowerCase() === "rejected" ? "danger" :
                        "warning"
                      }>
                        {staff.reviewsts || "Pending"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Tab>
      </Tabs>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Attendance Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMember && (
            <div>
              <Row>
                <Col md={6}>
                  <h5>Employee Information</h5>
                  <p><strong>Name:</strong> {selectedMember.EmployeeName}</p>
                  <p><strong>ID:</strong> {selectedMember.EMPCODE}</p>
                  <p><strong>Type:</strong> {selectedMember.AttType}</p>
                  <p><strong>Entry Date:</strong> {new Date(selectedMember.AttDateTime).toLocaleDateString()}</p>
                  <p><strong>Entry Time:</strong> {new Date(selectedMember.AttDateTime).toLocaleTimeString()}</p>
                  {selectedMember.AttLocation && (
                    <p>
                      <strong>Location:</strong>{" "}
                      <Button
                        variant="link"
                        className="p-0"
                        onClick={() => openMapWithLatLng(selectedMember.AttLocation)}
                      >
                        <FaMapMarkerAlt className="text-danger me-1" />
                        View on Map
                      </Button>
                    </p>
                  )}
                </Col>
                <Col md={6}>
                  {selectedMember.AttImage && (
                    <img
                      src={`${process.env.REACT_APP_API_URL}${selectedMember.AttImage}`}
                      alt="Attendance"
                      className="img-fluid rounded"
                    />
                  )}
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default StaffDailyAttendanceListScreen; 